<template>
    <div v-loading="loading" align="center">
        <h2><b>Welcome {{ user.displayName }}!</b> <br /> You have been successfully authenticated.</h2>
        <el-card width="100%" class="profile-card" v-if="authenticated">

            <div slot="header">
              <div :gutter="20" class="mt-16">
                <div :span="20" v-if="isPlayerOrCoach()">
                  <div class="warning-box" v-if="!checksCompletedAndValid">
                    <b class="text-medium" style="color: red;">To access the Player Zone please review and complete each of the following requirements.</b>
                  </div>
                  <div class="success-box" v-else>
                    <el-button class="link-button" @click="openPlayerZone" >All checks valid - go to Player Zone </el-button>
                  </div>
                </div>
                <div :span="10" v-else>
                  <div class="success-box">
                    <el-button class="link-button" @click="openPlayerZone" >All checks valid - go to Player Zone </el-button>
                  </div>
                </div>
              </div>
            </div>

            <el-card width="100%" class="profile-card" v-if="isPlayerOrCoach() && isCoachValidToAccess">
              <div slot="header">
                <b>Payment Status</b>
              </div>
                <div v-if="isPaymentDue() === false">Paid or Postponed</div>
                <div v-else>
                    Your payments are due. Please proceed to <a class="request-link" href="/player-payment">Payments and Subscriptions</a> in order to either extend your payment
                    schedule or pay.
                </div>
            </el-card>

            <br/>

            <el-card width="50%" class="profile-card" v-if="isPlayer()">
              <div slot="header" >
                <b>International Tennis Integrity Agency</b>
              </div>
              <div v-if="isTiuStatusValid()">Valid</div>
              <div v-else>
                  Your interview is due and needs to be completed. Please proceed to <a class="request-link" v-bind:href="user.tiuEnvelopeUrl">your ITIA Interview</a> to update
                  your status.
              </div>
            </el-card>

          <br/>

          <!--TO DO Remove isUserEnableToSignTheAnnualForm for Deployment of the Annual Forms-->
          <el-card width="50%" class="profile-card" v-if="isPlayer() && isUserEnableToSignTheAnnualForm">
              <div slot="header" >
                  <b>Player Annual Form</b>
              </div>
              <div v-if="hasThePlayerAnnualConsentFormBeenSigned">Valid</div>
              <div v-else-if="loadingPlayerAnnualConsentFormError === null">
                  Your Player Annual Form are due and need to be completed. Please proceed to
                  <a class="request-link" :href="playerAnnualFormSharepointUrl">
                      your Player Annual Form
                  </a>to update your status.
              </div>
              <div v-else>
                  {{ loadingPlayerAnnualConsentFormError }}
              </div>
          </el-card>

          <br/>
          <!--TO DO Remove isUserEnableToSignTheAnnualForm for Deployment of the Annual Forms-->
          <HipaaFormValidation v-if="isPlayer() && isUserEnableToSignTheAnnualForm" :player-email="user.email" :player-i-d="user.employeeId" @is-hipaa-form-valid="setIsHipaaFormValid"></HipaaFormValidation>
          <br/>

          <el-row :gutter="20" v-if="isAgent()">
              <el-col :span="10" v-if="checksCompletedAndValid">
                  As an agent you can also act for the players associated to you.<br /><br />
                  Please select the player below and click on the button “Act for player and open Player Zone”. You will then be granted access to all Player
                  Zone functions available to that player. If you are missing player entries to select, then please contact the WTA. Your email you are using
                  for login ({{ user.email }}) might not be assigned to the player.
                  <div class="mt-8">
                      <el-select v-model="playerSelectedForImpersonation">
                          <el-option
                              v-for="player in playersAssociatedToAgent"
                              :key="player.id"
                              :label="player.lastName + ', ' + player.firstName"
                              :value="player.id"
                          ></el-option>
                      </el-select>
                      <el-button v-if="playerSelectedForImpersonation" class="link-button ml-8" @click="impersonatePlayerForAgent(false)"
                          >Act for Player and open Player Zone</el-button
                      >
                      <el-button v-if="playerSelectedForImpersonation" class="link-button ml-8" @click="impersonatePlayerForAgent(true)"
                          >Perform Payment for Player</el-button
                      >
                  </div>
              </el-col>
              <el-col :span="10" v-else>
                  <b class="text-small" style="color: red;">Please review the actions required</b>
              </el-col>
          </el-row>

          <el-row :gutter="20" v-if="isCoach() && !isCoachValidToAccess">
            <el-col :span="20">
              <div class="warning-message">
              <p class="text-small">
                You need to have an employee ID to access the system. Please create a ticket to the WTA to get one:
              </p>
              <a
                  :href="ticketFormUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  class="request-link"
              >
                Create Request
              </a>
              </div>
            </el-col>
          </el-row>

        </el-card>

        <div class="border p-16 mt-15" v-if="authenticated && (mode === 'uat' || mode === 'development')" style="opacity: 0.7">
            The following functionality is only visible in UAT and Development. It's purpose is to provide quick access to functionalities that Players, Agent
            and Coaches get through links in Player Zone Sharepoint Site.
            <template>
                <br /><br />Set another player identifier <input type="text" v-model="overwrittenPlayerIdentifier" />
                <input type="submit" @click="setPlayerIdentifier" value="Save" /><input
                    type="submit"
                    @click="clearPlayerIdentifier"
                    value="Reset to Original"
                />
                for this session only.
            </template>
            <div class="mt-16">
                <code>
                    {{ user.displayName }} PY: {{ paymentInformation.plyrNum }} R: {{ user.role }}-{{ paymentInformation.role }}, E:
                    {{ paymentInformation.employeeId }}, OV: {{ overwrittenPlayerIdentifier }}, M: {{ mode }}, WM: {{ paymentInformation.witaMember }} PD:
                    {{ paymentInformation.paymentDate }} OM: {{ paymentInformation.owesMoney }} US: {{ paymentInformation.username }}
                </code>
            </div>
            <el-row :gutter="10" class="mt-32">
                <el-col :span="6">
                    <el-card>
                        <div slot="header">
                            <b>Profile and Ranking Summaries</b>
                        </div>
                        <div class="text-small card-text">
                            This app allows you to see all information we have stored on your account. Also it provides information about your ranking details
                            and other players ranking summaries.
                        </div>
                        <el-button @click="openProfile" class="link-button">Open </el-button>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card>
                        <div slot="header">
                            <b>Membership Payment and Subscription</b>
                        </div>
                        <div class="text-small card-text">
                            Your membership payments and subscriptions can be managed through here.
                        </div>
                        <el-button @click="openPayment" class="link-button">Open</el-button>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card>
                        <div slot="header">
                            <b>Singles and Doubles Entries</b>
                        </div>
                        <div class="text-small card-text">
                            This page allows you to enter Singles and Doubles tournaments. In addition to that you can withdraw entries and see the history.
                        </div>
                        <el-button @click="openEntry" class="link-button">
                            Open
                        </el-button>
                    </el-card>
                </el-col>
                <el-col :span="6">
                    <el-card>
                        <div slot="header">
                            <b>Acceptance and Preliminary Lists</b>
                        </div>
                        <div class="text-small card-text">
                            This app enables you to see, for any Singles or Doubles tournament, the preliminary list and the acceptance.
                        </div>
                        <el-button @click="openAcceptance" class="link-button">Open</el-button>
                    </el-card>
                </el-col>
            </el-row>
        </div>
    </div>
</template>

<script>
import * as axios from 'axios';
import LogRocket, {error} from 'logrocket';
import {mapGetters} from "vuex";
import HipaaFormValidation from './HipaaFormValidation.vue';

export default {
    components: { HipaaFormValidation },
    data() {
        return {
            mode: process.env.VUE_APP_APPLICATION_MODE,
            url: process.env.VUE_APP_API_URL,
            version: null,
            loading: true,
            loadingAuth: false,
            user: {
                displayName: '',
                employeeId: '',
                roles: [],
                tiuVerified: false,
                tiuEnvelopeUrl: '',
                email: '',
            },
            isCoachValidToAccess: false,
            paymentInformation: {},
            authenticated: false,
            overwrittenPlayerIdentifier: '',
            playersAssociatedToAgent: [],
            playerSelectedForImpersonation: '',
            annualFormValidation: {},
            hipaaAnnualFormValidation: false,
            codesFromPlayerAnnualFormsToAble: [
              "PlayerConsentForm.PlayerAlreadySignedForm",
              "PlayerConsentForm.PlayerNotIsEnableToSign",
              "PlayerConsentForm.PlayerNotOfLegalAge",
            ],
            ticketFormUrl: process.env.VUE_APP_REQUEST_TICKET_URL,
            playerAnnualFormSharepointUrl: process.env.VUE_APP_SHAREPOINT_URL_PLAYER_ANNUAL_FORMS,
        };
    },
    created() {
        this.loadingAuth = true;
        this.$msal.authenticateRedirect().then((data) => {
            this.$msal.getSilentToken(data[0], ['User.Read']).then((token) => {
                console.log('Authentication flow triggered: After silent token');
                this.$msal
                    .callMSGraph('https://graph.microsoft.com/v1.0/me?$select=displayName,givenName,employeeId', token.accessToken)
                    .then((graphResponse) => {
                        graphResponse.json().then((jsonResponse) => {
                            this.setupEnvironment(token, jsonResponse);
                            localStorage.setItem('jwt', token.idToken);
                            localStorage.setItem('accessToken', token.accessToken);
                            if (!this.isPlayerOrCoach() && !this.isAgent()) {
                                this.openWtaZone();
                            } else {
                              this.authenticated = true;
                                if (this.isAgent()) {
                                    this.loadAgentImpersonationData();
                                } else {
                                    if(this.isCoach())
                                    {
                                      if(this.user.employeeId === null || this.user.employeeId === '')
                                      {
                                        this.isCoachValidToAccess = false;
                                        this.authenticated = true;
                                        this.$message({
                                            showClose: true,
                                            message: 'You need to have an employee ID to access the system. Please create a ticket to the WTA to get one.',
                                            type: 'error',
                                            duration: 2500,
                                        });
                                        this.loading = false;
                                        this.loadingAuth = false;
                                      } else {
                                        this.isCoachValidToAccess = true;
                                        this.loadPaymentAndStatusInformation();
                                        this.loadAnnualFormsValidation();
                                      }
                                    }else {
                                      this.loadPaymentAndStatusInformation();
                                      this.loadAnnualFormsValidation();
                                      this.loading = false;
                                      this.loadingAuth = false;
                                      this.isCoachValidToAccess = true;
                                    }
                                }
                            }
                        });
                    });
            });
        });
    },
    computed: {
        checksCompletedAndValid() {
            if(this.isCoach())
            {
              return this.isCoachValidToAccess && !this.isPaymentDue();
            }
            if (this.isPlayerOrCoach()) {
                let tiuValid = true;
                if (this.isPlayer()) {
                    tiuValid = this.isTiuStatusValid();
                }

                //TO DO Ad back the commented code for the deployment of the Annual Forms
                return !this.isPaymentDue() && tiuValid;// && this.isValidTheStatusFromPlayerAnnualForms() && this.isHipaaFormValidToContinue();
            }
            return true;
        },
      ...mapGetters('information', ['isUserEnableToSignTheAnnualForm','hasThePlayerAnnualConsentFormBeenSigned', 'loadingPlayerAnnualConsentFormError']),
          ...mapGetters('playerConsentFormStore', ['hasTheHipaaConsentFormBeenSigned']),
    },
    methods: {
        isHipaaFormValidToContinue() {
            if (!this.isPlayer()) {
                return true;
            }
            return this.hasTheHipaaConsentFormBeenSigned;
        },
        setIsHipaaFormValid(value) {
            this.hipaaAnnualFormValidation = this.isPlayer() ? value : true;
        },
        isValidTheStatusFromPlayerAnnualForms(){
          if (!this.annualFormValidation.code) {
            return true;
          }
          return this.codesFromPlayerAnnualFormsToAble.some(str => this.annualFormValidation.code.includes(str));
        },
        setupEnvironment(token, jsonResponse) {
            const d = false;
            if (!d) {
                this.$set(this.user, 'roles', token.idTokenClaims.roles);
                this.$set(this.user, 'displayName', jsonResponse.displayName);
                const employeeIdFromAd = jsonResponse.employeeId;
                this.$set(this.user, 'employeeId', employeeIdFromAd);
                this.$set(this.user, 'email', token.idTokenClaims.preferred_username);
                if (localStorage.getItem('overwrittenPlayerIdentifier') !== null) {
                    this.$set(this.user, 'employeeId', localStorage.getItem('overwrittenPlayerIdentifier'));
                } else {
                    this.$set(this.user, 'employeeId', employeeIdFromAd);
                    window.playerIdentifier = employeeIdFromAd;
                }
                LogRocket.identify(employeeIdFromAd, {
                    name: jsonResponse.displayName,
                    email: token.idTokenClaims.preferred_username,
                    roleType: token.idTokenClaims.roles,
                    overwrittenType: localStorage.getItem('overwrittenPlayerIdentifier') !== null,
                });
            } else {
                this.$set(this.user, 'roles', ['']);
                this.$set(this.user, 'displayName', 'xxx');
                this.$set(this.user, 'email', 'xxx');
                const employeeIdFromAd = '';
                this.$set(this.user, 'employeeId', employeeIdFromAd);
                window.playerIdentifier = employeeIdFromAd;
            }
        },
        impersonatePlayerForAgent(showPayment) {
            if (this.playerSelectedForImpersonation) {
                localStorage.setItem('overwrittenPlayerIdentifier', this.playerSelectedForImpersonation);
                if (showPayment) {
                    this.openPayment();
                } else {
                    this.openPlayerZone();
                }
            }
        },
        loadAgentImpersonationData() {
            this.$store.dispatch('information/retrievePlayersAssociatedToAgent', { userEmail: this.user.email }).then((data) => {
                this.loading = false;
                this.loadingAuth = false;
                this.authenticated = true;
                this.playersAssociatedToAgent = data;
            });
        },
        loadPaymentAndStatusInformation() {
            this.$store
                .dispatch('information/retrievePaymentInformation', { playerIdentifier: this.user.employeeId })
                .then((data) => {
                    this.paymentInformation = data;
                      this.$store
                          .dispatch('information/retrieveTennisIntegrityUnitStatus', { playerIdentifier: this.user.employeeId })
                          .then((tiuData) => {
                            if (tiuData.testVerified !== true) {
                              this.user.tiuVerified = false;
                              this.user.tiuEnvelopeUrl = tiuData.encryptedEnvelopeUrl;
                            } else {
                              this.user.tiuVerified = true;
                            }
                            this.loading = false;
                            this.loadingAuth = false;
                            this.authenticated = true;
                            this.playersAssociatedToAgent = data;
                            if (this.checksCompletedAndValid && this.mode === 'production') {
                              setTimeout(() => this.openPlayerZone(), 250);
                            }
                          })
                          .catch((e) => {
                            console.log("Error from TIU: ", { e });
                            console.log('Got error from Baseline for TIU data');
                            this.loading = false;
                            this.loadingAuth = false;
                            this.$message({
                              showClose: true,
                              message: 'Could not retrieve information from WTA backend systems. Your database entry seems to be erroneous.',
                              type: 'error',
                              duration: 0,
                            });
                          });
                })
                .catch((e) => {
                    console.log('Got error from Baseline');
                    this.loading = false;
                    this.loadingAuth = false;
                    this.$message({
                        showClose: true,
                        message:
                            'Could not retrieve information from WTA backend systems. ' +
                            'Retrying in 5 seconds. If the problem persists then contact the Administrator',
                        type: 'error',
                        duration: 0,
                    });
                    window.setTimeout(() => {
                        localStorage.clear();
                        sessionStorage.clear();
                        window.location.href = '/';
                    }, 5000);
                });
        },
        loadAnnualFormsValidation() {
            this.$store
                .dispatch('information/retrieveAnnualFormValidation', { playerIdentifier: this.user.employeeId, playerEmail: this.user.email })
                .then((data) => {
                    this.annualFormValidation = data;
                })
                .catch((e) => {
                    this.loading = false;
                    this.loadingAuth = false;
                    this.$message({
                        showClose: true,
                        message:
                            'Could not retrieve information from WTA backend systems. ' +
                            'Retrying in 5 seconds. If the problem persists then contact the Administrator',
                        type: 'error',
                        duration: 0,
                    });
                   window.setTimeout(() => {
                        localStorage.clear();
                        sessionStorage.clear();
                        window.location.href = '/';
                    }, 5000);
                });
        },

        isPaymentDue() {
            return this.paymentInformation.paymentRequiredNow;
        },
        isTiuStatusValid() {
            return this.user.tiuVerified;
        },
        isPlayerOrCoach() {
            return this.user.roles !== undefined && (this.user.roles.indexOf('Players') >= 0 || this.user.roles.indexOf('Coaches') >= 0);
        },

        isPlayer() {
            return this.user.roles !== undefined && this.user.roles.indexOf('Players') >= 0;
        },

        isCoach() {
          console.log("Role", this.user.roles);
            return this.user.roles !== undefined && this.user.roles.indexOf('Coaches') >= 0;
        },

        isAgent() {
            return this.user.roles !== undefined && this.user.roles.indexOf('Agent') >= 0;
        },

        openAcceptance() {
            window.location.href = '/prelim-and-al';
            return true;
        },

        openEntry() {
            window.location.href = '/player-entries';
            return true;
        },

        openPayment() {
            window.location.href = '/player-payment';
            return true;
        },

        openProfile() {
            window.location.href = '/player-profile';
            return true;
        },

        openWtaZone() {
            window.location.href = 'https://wtatennis.sharepoint.com/sites/wta-zone';
            return true;
        },

        openPlayerZone() {
            window.location.href = process.env.VUE_APP_URL_HOME;
            return true;
        },

        setPlayerIdentifier() {
            this.user.employeeId = this.overwrittenPlayerIdentifier;
            this.loading = true;
            localStorage['overwrittenPlayerIdentifier'] = this.overwrittenPlayerIdentifier;
            this.loadPaymentAndStatusInformation();
        },

        clearPlayerIdentifier() {
            this.loading = true;
            localStorage.removeItem('overwrittenPlayerIdentifier');
            window.location.reload();
        },
    },
};
</script>

<style>
.el-card {
    min-height: 100px;
}

.card-text {
    padding: 5px 0px 20px 0px;
    min-height: 80px;
}

.profile-card .el-row {
    padding-top: 10px;
}

.start-card {
    background-color: #faf6ff !important;
}

.link-button {
    font-size: 16px !important;
    color: #7814ff !important;
    font-weight: bold !important;
}

.links[_ngcontent-poh-c120] {
    margin-top: -40px;
    margin-left: 100px;
    margin-right: 100px;
    display: flex;
    align-items: center;
    justify-content: space-evenly;
    background-color: #faf6ff;
    height: 300px;
}

.top-label[_ngcontent-poh-c118] {
    position: absolute;
    width: 300px;
    height: 70px;
    color: #fff;
    background-color: #000;
    margin-top: -40px;
    margin-left: 20px;
    padding-left: 10px;
    padding-top: 10px;
    font-size: 16px;
    display: flex;
    align-items: center;
}

.top-label-text[_ngcontent-poh-c118] {
    color: #fff;
    font-weight: 550;
    line-height: 1.2;
    font-size: 20px;
    font-family: Work Sans, Arial, Helvetica Neue, sans-serif;
    text-align: center;
}

.background-image[_ngcontent-poh-c118] {
    min-width: 350px;
    max-width: 350px;
    vertical-align: middle;
}

a:not([href]):not([class]),
a:not([href]):not([class]):hover {
    color: inherit;
    text-decoration: none;
}

a[_ngcontent-poh-c118] {
    cursor: pointer;
}

.bot-label[_ngcontent-poh-c118] {
    position: absolute;
    width: 250px;
    height: 45px;
    color: #000;
    background-color: #d4bbfe;
    margin-top: -20px;
    margin-left: 20px;
    padding-left: 10px;
    padding-top: 10px;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.bot-label-text[_ngcontent-poh-c118] {
    color: #000;
    text-transform: uppercase;
    font-weight: 550;
    line-height: 1.2;
    font-size: 16px;
    font-family: Work Sans, Arial, Helvetica Neue, sans-serif;
    text-align: center;
}

a[_ngcontent-poh-c136] {
    cursor: pointer;
}

.bot-label[_ngcontent-poh-c136] {
    position: absolute;
    width: 250px;
    height: 55px;
    color: #000;
    background-color: #d4bbfe;
    margin-top: 50px;
    margin-left: 20px;
    padding-left: 10px;
    padding-top: 10px;
    font-size: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.bot-label-text[_ngcontent-poh-c136] {
    color: #000;
    text-transform: uppercase;
    font-weight: 550;
    line-height: 1.2;
    font-size: 16px;
    font-family: Work Sans, Arial, Helvetica Neue, sans-serif;
    text-align: center;
}
.warning-message {
  background-color: #fff4f4;
  border: 1px solid #f56c6c;
  padding: 15px;
  border-radius: 5px;
  display: flex;
  align-items: center;
}

.request-link {
  color: #7814ff;
  text-decoration: none;
  font-weight: bold;
}

.request-link:hover {
  text-decoration: underline;
}

.warning-box {
  border: 1px solid red;
  background-color: #ffe6e6;
  padding: 10px;
  margin-top: 10px;
  border-radius: 5px;
}

.success-box {
  border: 1px solid green;
  background-color: #ebffe6;
  padding: 10px;
  margin-top: 10px;
  border-radius: 5px;
}

</style>
